import { useCallback, useEffect, useRef, useState } from 'react';

const LEAVE_ANIMATION_MS = 180;
const ENTER_ANIMATION_MS = 400;

/**
 * Which members in the payments table show their other policies.
 * `entering` / `leaving` let the table animate the rows in and out.
 */
export function useExpandedMembers() {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [entering, setEntering] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const toggle = useCallback(
    (memberId: string) => {
      if (leaving) return;
      clearTimeout(timer.current);

      if (openIds.has(memberId)) {
        setLeaving(memberId);
        timer.current = setTimeout(() => {
          setOpenIds((current) => {
            const next = new Set(current);
            next.delete(memberId);
            return next;
          });
          setLeaving(null);
        }, LEAVE_ANIMATION_MS);
        return;
      }

      setOpenIds((current) => new Set(current).add(memberId));
      setEntering(memberId);
      timer.current = setTimeout(() => setEntering(null), ENTER_ANIMATION_MS);
    },
    [openIds, leaving]
  );

  return { openIds, entering, leaving, toggle };
}
